import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ChevronDown, ChevronUp, Circle, Hammer, Loader2, MapPin, Target } from "lucide-react";
import { fmtMXN, fmtNum, fmtPct } from "./format";

export function GoalBar({ value, goal, pct, compact = false }) {
  const p = Math.min(pct ?? 0, 100);
  return (
    <div data-testid="inv-goal-bar">
      {!compact && (
        <div className="flex items-end justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono flex items-center gap-1.5"><Target size={11} /> Meta mensual</div>
          <div className="font-mono text-xs text-neutral-300 tabular-nums">{fmtMXN.format(value)} <span className="text-neutral-600">/ {goal ? fmtMXN.format(goal) : "sin meta"}</span></div>
        </div>
      )}
      <div className="h-2 rounded-full bg-white/6 overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${p}%` }} transition={{ duration: 0.9, ease: "easeOut" }} className={`h-full rounded-full ${p >= 100 ? "bg-emerald-400" : "bg-[#FFBF57]"}`} />
      </div>
      <div className="mt-1.5 font-mono text-[11px] text-neutral-400 tabular-nums">{pct == null ? "Meta no configurada" : `${fmtPct(pct)} alcanzado`}</div>
    </div>
  );
}

function Row({ label, value, strong, negative, sub }) {
  return (
    <div className={`flex items-baseline justify-between py-2.5 ${strong ? "border-t border-white/10 mt-1 pt-3" : ""}`}>
      <div>
        <div className={`text-sm ${strong ? "text-white" : "text-neutral-400"}`}>{label}</div>
        {sub && <div className="text-[10px] text-neutral-600 font-mono mt-0.5">{sub}</div>}
      </div>
      <div className={`font-mono tabular-nums ${strong ? "text-lg text-white" : "text-sm text-neutral-200"} ${negative ? "text-rose-300" : ""}`}>{negative ? "−" : ""}{fmtMXN.format(Math.abs(value || 0))}</div>
    </div>
  );
}

const CAT_LABEL = { renta: "Renta", nomina: "Nómina", servicios: "Servicios", marketing: "Marketing", mantenimiento: "Mantenimiento", insumos: "Insumos", impuestos: "Impuestos", comisiones: "Comisiones", general: "Otros gastos" };
export const catLabel = (c) => CAT_LABEL[c] || (c ? c.charAt(0).toUpperCase() + c.slice(1) : "Otros gastos");

const BASIS = {
  real: (m) => "Costo real por venta reportado por RubiPOS",
  compras: (m) => `Compras de insumos / traspasos recibidos de RubiPOS (${m.compras_n || 0} traspaso${m.compras_n === 1 ? "" : "s"})`,
  estimado: (m) => (m.cobertura_costo_pct > 0 ? `${fmtPct(m.cobertura_costo_pct, 0)} costo real · resto proyectado con margen ${fmtPct(m.margen_estimado_pct, 0)}` : `Proyectado con margen bruto ${fmtPct(m.margen_estimado_pct ?? 45, 0)}`),
  mixto: (m) => "Combinación por sucursal: costo real, traspasos recibidos o proyectado (ver cada sucursal)",
};
const BASIS_TAG = { real: "Real", compras: "Traspasos", estimado: "Proyectado", mixto: "Mixto" };

export function PnlCard({ m, title = "Estado de resultados · mes en curso" }) {
  const [openGastos, setOpenGastos] = useState(true);
  const ventas = m.ventas ?? m.month_ventas ?? 0;
  const detalle = m.gastos_detalle || [];
  const basis = m.cogs_basis || "estimado";
  const costoSub = (BASIS[basis] || BASIS.estimado)(m);
  return (
    <div className="rounded-2xl border border-white/8 bg-[#111112] p-6" data-testid="inv-pnl-card">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">{title}</div>
        <span className={`text-[9px] uppercase tracking-widest font-mono px-2 py-0.5 rounded-full border ${basis === "estimado" ? "border-amber-400/40 text-amber-300" : "border-emerald-400/40 text-emerald-300"}`} data-testid="inv-pnl-basis">Costo {BASIS_TAG[basis] || basis}</span>
      </div>
      <div className="mt-3">
        <Row label="Ventas" value={ventas} sub={`${fmtNum.format(m.tickets || 0)} tickets${m.ticket_promedio ? ` · ticket promedio ${fmtMXN.format(m.ticket_promedio)}` : ""}`} />
        <Row label="Costo de mercancía" value={m.cogs} negative sub={costoSub} />
        {(m.compras > 0 || m.transito > 0) && basis !== "compras" && (
          <div className="text-[10px] font-mono text-neutral-500 -mt-1 mb-1 pl-3 border-l border-white/10" data-testid="inv-pnl-compras-info">Compras/traspasos recibidos {fmtMXN.format(m.compras || 0)}{m.transito > 0 ? ` · en tránsito ${fmtMXN.format(m.transito)}` : ""}</div>
        )}
        {basis === "compras" && (m.transito > 0 || m.incidencias_n > 0) && (
          <div className="text-[10px] font-mono text-neutral-500 -mt-1 mb-1 pl-3 border-l border-white/10" data-testid="inv-pnl-compras-info">{m.transito > 0 ? `En tránsito (enviado, aún no recibido): ${fmtMXN.format(m.transito)}` : ""}{m.transito > 0 && m.incidencias_n > 0 ? " · " : ""}{m.incidencias_n > 0 ? `${m.incidencias_n} traspaso${m.incidencias_n === 1 ? "" : "s"} con incidencia` : ""}</div>
        )}
        <Row label="Utilidad bruta" value={m.utilidad_bruta} strong sub={`Margen bruto ${fmtPct(m.margen_bruto_pct ?? (ventas ? (m.utilidad_bruta / ventas) * 100 : null))}`} />
        <div className="py-2.5">
          <button onClick={() => setOpenGastos(!openGastos)} className="w-full flex items-baseline justify-between text-left" data-testid="inv-pnl-gastos-toggle">
            <div>
              <div className="text-sm text-neutral-400 flex items-center gap-1.5">Gastos operativos {detalle.length > 0 && (openGastos ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}</div>
              <div className="text-[10px] text-neutral-600 font-mono mt-0.5">{detalle.length ? `${detalle.length} categorías · ${fmtNum.format(detalle.reduce((a, d) => a + d.n, 0))} movimientos` : "Sin gastos registrados este mes"}</div>
            </div>
            <div className="font-mono tabular-nums text-sm text-rose-300">−{fmtMXN.format(m.gastos || 0)}</div>
          </button>
          {openGastos && detalle.length > 0 && (
            <ul className="mt-2 space-y-1.5 border-l border-white/10 ml-1 pl-3" data-testid="inv-pnl-gastos-detalle">
              {detalle.map((d) => (
                <li key={d.categoria} className="flex items-center justify-between text-xs">
                  <span className="text-neutral-500">{catLabel(d.categoria)} <span className="text-neutral-700 font-mono">· {ventas ? fmtPct((d.monto / ventas) * 100) : ""}</span></span>
                  <span className="font-mono tabular-nums text-neutral-300">{fmtMXN.format(d.monto)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Row label="Utilidad neta" value={m.utilidad_neta} strong negative={m.utilidad_neta < 0} />
      </div>
      <div className="mt-3 text-[11px] font-mono text-neutral-500">Margen neto {fmtPct(m.margen_neto_pct ?? (ventas ? (m.utilidad_neta / ventas) * 100 : null))}</div>
    </div>
  );
}

export function BranchCard({ b, active, onClick }) {
  const pre = b.preopening;
  return (
    <button onClick={onClick} className={`text-left rounded-2xl border p-5 transition-colors ${active ? "border-[#FFBF57]/60 bg-[#FFBF57]/[0.04]" : "border-white/8 bg-[#111112] hover:border-white/20"}`} data-testid={`inv-branch-card-${b.branch_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display text-lg text-white leading-tight">{b.name}</div>
          <div className="text-[11px] text-neutral-500 mt-1 flex items-center gap-1"><MapPin size={10} /> {[b.city, b.state].filter(Boolean).join(", ") || "—"}</div>
        </div>
        <span className={`text-[9px] uppercase tracking-widest font-mono px-2 py-1 rounded-full border ${pre ? "border-sky-400/40 text-sky-300" : "border-emerald-400/40 text-emerald-300"}`}>{pre ? "Pre-apertura" : "Operando"}</span>
      </div>
      {pre ? (
        <div className="mt-4">
          <div className="flex items-center justify-between text-[11px] font-mono text-neutral-400"><span className="flex items-center gap-1"><Hammer size={11} /> Avance de apertura</span><span>{pre.done}/{pre.total} etapas</span></div>
          <div className="mt-2 h-2 rounded-full bg-white/6 overflow-hidden"><div className="h-full bg-sky-400 rounded-full" style={{ width: `${(pre.done / (pre.total || 1)) * 100}%` }} /></div>
          {pre.target_open_date && <div className="text-[10px] text-neutral-500 font-mono mt-2">Apertura estimada · {new Date(`${pre.target_open_date}T12:00:00`).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}</div>}
          <div className="text-[11px] text-neutral-500 mt-2">Tu participación: <span className="text-[#FFBF57] font-mono">{b.pct}%</span> de la utilidad neta al abrir.</div>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            {[["Hoy", b.today.ventas], ["Semana", b.week.ventas], ["Mes", b.month.ventas]].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-black/30 border border-white/5 py-2">
                <div className="text-[9px] uppercase tracking-widest text-neutral-600 font-mono">{l}</div>
                <div className="font-mono text-xs text-white mt-0.5 tabular-nums">{fmtMXN.format(v)}</div>
              </div>
            ))}
          </div>
          <GoalBar value={b.month.ventas} goal={b.monthly_goal} pct={b.goal_progress_pct} compact />
          <div className="flex items-center justify-between text-xs border-t border-white/5 pt-3">
            <span className="text-neutral-500">Tu corte · <span className="text-[#FFBF57] font-mono">{b.pct}%</span></span>
            <span className="font-mono text-white tabular-nums">{fmtMXN.format(b.cut)}</span>
          </div>
        </div>
      )}
    </button>
  );
}

export function DailyCuts({ series, today }) {
  const rows = [...series].filter((d) => d.date <= today && (d.tickets > 0 || d.ventas > 0)).reverse();
  return (
    <div className="rounded-2xl border border-white/8 bg-[#111112] p-6" data-testid="inv-daily-cuts">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-3">Cortes diarios · {rows.length} días con venta</div>
      <div className="max-h-72 overflow-y-auto pr-1">
        <table className="w-full text-xs">
          <thead className="text-[9px] uppercase tracking-widest text-neutral-600 font-mono sticky top-0 bg-[#111112]">
            <tr><th className="text-left py-2">Día</th><th className="text-right py-2">Tickets</th><th className="text-right py-2">Ticket prom.</th><th className="text-right py-2">Venta</th></tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.map((d) => (
              <tr key={d.date} data-testid={`inv-cut-row-${d.date}`}>
                <td className="py-2 text-neutral-300 font-mono">{new Date(`${d.date}T12:00:00`).toLocaleDateString("es-MX", { weekday: "short", day: "2-digit", month: "short" })}</td>
                <td className="py-2 text-right font-mono text-neutral-400 tabular-nums">{fmtNum.format(d.tickets)}</td>
                <td className="py-2 text-right font-mono text-neutral-400 tabular-nums">{d.tickets ? fmtMXN.format(d.ventas / d.tickets) : "—"}</td>
                <td className="py-2 text-right font-mono text-white tabular-nums">{fmtMXN.format(d.ventas)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-neutral-600 italic">Sin ventas registradas en este mes.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CortesCaja({ cortes, branches }) {
  const names = Object.fromEntries((branches || []).map((b) => [b.branch_id, b.name]));
  return (
    <div className="rounded-2xl border border-white/8 bg-[#111112] p-6" data-testid="inv-cortes-caja">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-3">Cortes de caja · RubiPOS · {cortes.length} cierres este mes</div>
      {cortes.length === 0 ? <div className="text-xs text-neutral-600 italic">RubiPOS aún no ha enviado cortes de caja para este mes.</div> : (
        <div className="max-h-72 overflow-y-auto pr-1">
          <table className="w-full text-xs">
            <thead className="text-[9px] uppercase tracking-widest text-neutral-600 font-mono sticky top-0 bg-[#111112]">
              <tr><th className="text-left py-2">Cierre</th><th className="text-left py-2">Caja</th><th className="text-right py-2">Tickets</th><th className="text-right py-2">Venta</th><th className="text-right py-2">Dif. efectivo</th></tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {cortes.map((c) => (
                <tr key={c.corte_id} data-testid={`inv-corte-row-${c.corte_id}`}>
                  <td className="py-2 text-neutral-300 font-mono">{new Date(c.fecha).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}{branches?.length > 1 && <div className="text-[9px] text-neutral-600 truncate max-w-[140px]">{names[c.branch_id]}</div>}</td>
                  <td className="py-2 text-neutral-400">{c.caja || "—"}{c.cajero ? <div className="text-[9px] text-neutral-600 truncate max-w-[120px]">{c.cajero}</div> : null}</td>
                  <td className="py-2 text-right font-mono text-neutral-400 tabular-nums">{c.tickets ?? "—"}</td>
                  <td className="py-2 text-right font-mono text-white tabular-nums">{fmtMXN.format(c.ventas_total || 0)}</td>
                  <td className={`py-2 text-right font-mono tabular-nums ${c.diferencia == null ? "text-neutral-600" : c.diferencia < 0 ? "text-rose-300" : c.diferencia > 0 ? "text-emerald-300" : "text-neutral-400"}`}>{c.diferencia == null ? "—" : `${c.diferencia > 0 ? "+" : ""}${fmtMXN.format(c.diferencia)}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function PreopeningStages({ stages }) {
  const icon = (s) => (s === "done" ? <CheckCircle2 size={14} className="text-emerald-400" /> : s === "in_progress" ? <Loader2 size={14} className="text-sky-400 animate-spin" /> : <Circle size={14} className="text-neutral-600" />);
  return (
    <ol className="grid sm:grid-cols-2 gap-2" data-testid="inv-preopening-stages">
      {stages.map((s) => (
        <li key={s.key} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-sm ${s.status === "done" ? "border-emerald-400/20 text-neutral-300" : s.status === "in_progress" ? "border-sky-400/30 text-white" : "border-white/5 text-neutral-500"}`}>
          {icon(s.status)} {s.label}
        </li>
      ))}
    </ol>
  );
}
